import { ReactElement, useEffect } from 'react';
import { getDehydratedState } from '../../common/getDehydrateState';
import { t } from '../../common/getTranslations';
import { DehydratedProps } from '../../common/type';
import useTypedDispatch from '../../common/useTypedDispatch';
import { Layout } from '../../design-system/layout/Layout';
import { FooterType } from '../../design-system/layout/LayoutFooter/FooterType.enum';
import { DayOfWeekLayoutContainer } from '../../domain/day-of-week/container/DayOfWeekLayoutContainer';
import { DayOfWeekPageViewHeadless } from '../../domain/day-of-week/headless/DayOfWeekPageViewHeadless';
import { DayOfWeekLayout } from '../../domain/day-of-week/presentational/DayOfWeekLayout';
import { DayOfWeekCommonProps, DayOfWeekReferenceKeyListProps } from '../../domain/day-of-week/type';
import { getDayId } from '../../domain/day-of-week/util/getDayId';
import { GnbMenuId } from '../../domain/menu/constant';
import { selectGnbMenu } from '../../domain/menu/util/MenuSlice';
import { ApiErrorBoundary } from '../../errors/ApiErrorBoundary.class';
import { addError } from '../../errors/ErrorSlice';
import { thunkSerializeError } from '../../errors/thunkSerializeError.function';
import { tabFactory } from '../../factory/tabFactory';
import { DayOfWeekLayout as GraphqlDayOfWeekLayout } from '../../graphql/generated/types';
import { CommonMobileHeaderType } from '../../header/constants';
import { CACHE_ONLY_CONFIG } from '../../reactQuery/constant';
import { createQueryClient } from '../../reactQuery/createQueryClient';
import { nextReduxWrapper } from '../../state/store';
interface DayOfWeekPageProps extends DehydratedProps, DayOfWeekCommonProps, DayOfWeekReferenceKeyListProps {
  tabId: string;
  isAllTab: boolean;
}
function DayOfWeekPage({
  tabId,
  isAllTab,
  ...rest
}: DayOfWeekPageProps) {
  const {
    dayId,
    sortType,
    referenceKeyList
  } = rest;
  const dispatch = useTypedDispatch();
  useEffect(() => {
    dispatch(selectGnbMenu(GnbMenuId.DayOfWeek));
  }, [dispatch]);
  if (isAllTab) {
    return <ApiErrorBoundary refreshOnRetry={true}>
        <DayOfWeekLayoutContainer dayId={dayId}>
          {data =>
        // @NOTE(@harry-hi): 탭 변경 시 routeChangeStart가 간헐적으로 동작하지 않아 ApiErrorBoundary의 에러 상태가 초기화되지 않는 문제 해결을 위해 key로 강제 리렌더링
        <DayOfWeekLayout key={`${dayId}-${tabId}-${sortType}`}>
              <DayOfWeekLayout.DayTab dayId={dayId} dayOfWeekLayout={data} />
              <DayOfWeekLayout.MenuListAndSortTab tabId={tabId} sortType={sortType} dayOfWeekLayout={data} />
              <ApiErrorBoundary refreshOnRetry={true}>
                <DayOfWeekLayout.Column {...rest} logProps={{
              tabId,
              dayId,
              sortType,
              dayOfWeekLayout: data
            }} />
              </ApiErrorBoundary>
              <DayOfWeekPageViewHeadless tabId={tabId} dayId={dayId} sortType={sortType} dayOfWeekLayout={data} />
            </DayOfWeekLayout>}
        </DayOfWeekLayoutContainer>
      </ApiErrorBoundary>;
  }
  const referenceKey = referenceKeyList[0];
  return <ApiErrorBoundary refreshOnRetry={true}>
      <DayOfWeekLayoutContainer dayId={dayId}>
        {data => <DayOfWeekLayout>
            <DayOfWeekLayout.DayTab dayId={dayId} tabId={tabId} sortType={sortType} dayOfWeekLayout={data} />
            <DayOfWeekLayout.MenuListAndSortTab tabId={tabId} sortType={sortType} dayOfWeekLayout={data} />
            <ApiErrorBoundary refreshOnRetry={true}>
              <DayOfWeekLayout.Category referenceKey={referenceKey} {...rest} logProps={{
            tabId,
            dayId,
            sortType,
            dayOfWeekLayout: data
          }} />
            </ApiErrorBoundary>
            <DayOfWeekPageViewHeadless tabId={tabId} dayId={dayId} sortType={sortType} dayOfWeekLayout={data} />
          </DayOfWeekLayout>}
      </DayOfWeekLayoutContainer>
    </ApiErrorBoundary>;
}
DayOfWeekPage.getInitialProps = nextReduxWrapper.getInitialPageProps(_store => async context => {
  const query = context.query;
  const {
    tab_id,
    day_id,
    sort_type
  } = query;
  let tabId = tab_id;
  const dayId = getDayId(day_id as string);
  const queryClient = createQueryClient();
  let dayOfWeekLayoutResult: GraphqlDayOfWeekLayout | null;
  try {
    dayOfWeekLayoutResult = await queryClient.fetchQuery(tabFactory.dayOfWeekLayout({
      dayUid: dayId
    }, {
      ...CACHE_ONLY_CONFIG
    }, {
      ssrRequest: context.req
    }));
  } catch (error) {
    _store.dispatch(addError({
      error: thunkSerializeError(error)
    }));
    // 리덕스에 에러 상태로 동기화했지만, 컴포넌트 렌더링을 위해 안전한 기본값으로 반환합니다.
    return {
      tabId,
      dayId,
      sortType: String(sort_type ?? ''),
      isAllTab: false,
      referenceKeyList: [],
      ...(queryClient && getDehydratedState(queryClient))
    };
  }
  const {
    selectedReference,
    selectedSort,
    referenceList
  } = dayOfWeekLayoutResult ?? {};
  const sortType = String(sort_type || selectedSort?.param || '');
  let referenceKeyList: string[] = [];
  // tabId 가 있으면 리스트에서 일치하는 리스트로 판단합니다.
  if (tabId) {
    referenceKeyList = (referenceList?.find(ref => ref.param === tabId)?.keys ?? []).filter(Boolean);
  }

  // tabId 로 탐색 후 리스트가 없다면 API 에서 내려준 선택된 기본 값으로 판단합니다.
  if (referenceKeyList.length === 0) {
    referenceKeyList = (selectedReference?.keys ?? []).filter(Boolean);
    tabId = selectedReference?.param ?? tabId;
  }

  // "전체" 탭인지 아닌지 판단합니다.
  const isAllTab = referenceKeyList.length > 1;

  // "전체" 탭의 경우 페이징 정보가 필요 없기 때문에 제거합니다.
  const page = isAllTab ? undefined : Number(query.page ?? 0);
  const size = isAllTab ? undefined : Number(query.size ?? 50);

  // 레퍼런스 키가 존재할 경우 레퍼런스 키 만큼 Day of week List API 호출합니다.
  if (referenceKeyList.length > 0) {
    const prefetchList: Promise<void>[] = [];
    for (const key of referenceKeyList) {
      const listVariables = {
        referenceKey: key,
        dayUid: dayId,
        sortType,
        page,
        size
      };
      if (isAllTab) {
        // "전체" 탭이면 페이징이 없다.
        prefetchList.push(queryClient.prefetchQuery(tabFactory.dayOfWeekList(listVariables, {
          ...CACHE_ONLY_CONFIG
        }, {
          ssrRequest: context.req
        })));
      } else {
        // 페이징 처리를 위해 infinite query 사용합니다.
        prefetchList.push(queryClient.prefetchInfiniteQuery(tabFactory.infiniteDayOfWeekList(listVariables, {
          ...CACHE_ONLY_CONFIG,
          initialPageParam: Number(page ?? 0),
          getNextPageParam: (lastPage, pages) => {
            if (lastPage && (lastPage.isEnd || lastPage.list.length === 0)) {
              return undefined;
            }
            return pages.length;
          }
        }, {
          ssrRequest: context.req
        })));
      }
    }
    try {
      await Promise.all(prefetchList);
    } catch (error) {
      _store.dispatch(addError({
        error: thunkSerializeError(error)
      }));
    }
  }
  return {
    tabId,
    dayId,
    sortType,
    page,
    size,
    referenceKeyList,
    isAllTab,
    ...(queryClient && getDehydratedState(queryClient))
  };
});
DayOfWeekPage.getLayout = function getLayout(page: ReactElement) {
  return <Layout title={t('common_tab_ongoing')} mobileLayoutConfig={{
    shouldShowHeader: true,
    shouldShowGnb: true,
    shouldShowFooter: true,
    headerType: CommonMobileHeaderType.MainAndSub,
    footerType: FooterType.DayOfWeek
  }} pcLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: true
  }} metaInfo={page.props.metaInfo}>
      {page}
    </Layout>;
};
export default DayOfWeekPage;